import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { LaunchDarklyService } from './launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { take } from 'rxjs/operators';

export interface RuntimeConfig {
  SecretKey: string;
  FlexKey: string | string[];
}

@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigService {
  private secretKey: string | null = null;
  private flexKey: string[] | null = null;
  private featureEnabled: boolean | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly ldService: LaunchDarklyService,
  ) {}

  /**
   * Resolves whether runtime secrets feature flag is enabled.
   * Caches the result so subsequent calls are synchronous.
   */
  private async resolveFeatureFlag(): Promise<boolean> {
    if (this.featureEnabled != null) return this.featureEnabled;
    try {
      const value = await this.ldService
        .getFlagValue(FEATURE_FLAGS.ENABLE_RUNTIME_SECRETS, false)
        .pipe(take(1))
        .toPromise();
      this.featureEnabled = !!value;
    } catch {
      this.featureEnabled = false;
    }
    return this.featureEnabled;
  }

  /**
   * Checks flag synchronously. Returns cached value or false if not yet resolved.
   */
  isFeatureEnabled(): boolean {
    return this.featureEnabled === true;
  }

  /**
   * Load config from API on app bootstrap.
   * When feature flag is OFF, skips the API call entirely.
   */
  async loadFromApi(): Promise<void> {
    const enabled = await this.resolveFeatureFlag();
    if (!enabled) return;

    try {
      const bootstrapToken = localStorage.getItem('_bt');
      const options = bootstrapToken
        ? { headers: { Authorization: `Bearer ${bootstrapToken}` } }
        : { withCredentials: true as const };

      const response = await this.http
        .get<{ Config?: string; SecretKey?: string; FlexKey?: string | string[] }>(
          `${environment.apiUrl}/api/config/client`,
          options,
        )
        .toPromise();
      if (response) {
        if (response.Config) {
          this.applyConfig(this.decodeConfig(response.Config));
        } else if (response.SecretKey && response.FlexKey != null) {
          this.secretKey = response.SecretKey;
          this.flexKey = this.normalizeFlexKey(response.FlexKey);
        }
      }
    } catch {
      // Config API may fail (e.g. 401 for unauthenticated users). App will get config from login response.
    }
  }

  /**
   * Load config from API using Bearer token (for Auth0/external login flow).
   * When feature flag is OFF, this is a no-op.
   */
  async loadFromApiWithToken(token: string): Promise<void> {
    const enabled = await this.resolveFeatureFlag();
    if (!enabled) return;

    const response = await this.http
      .get<{ Config?: string; SecretKey?: string; FlexKey?: string | string[] }>(`${environment.apiUrl}/api/config/client`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .toPromise();
    if (response) {
      if (response.Config) {
        this.applyConfig(this.decodeConfig(response.Config));
      } else if (response.SecretKey && response.FlexKey != null) {
        this.secretKey = response.SecretKey;
        this.flexKey = this.normalizeFlexKey(response.FlexKey);
      }
    }
  }

  /**
   * Load config from login/token API response.
   * When feature flag is OFF, this is a no-op.
   */
  loadFromLoginResponse(data: any): void {
    if (!this.isFeatureEnabled()) return;
    if (!data) return;
    if (data.SecretKey && data.FlexKey != null) {
      this.secretKey = data.SecretKey;
      this.flexKey = this.normalizeFlexKey(data.FlexKey);
      return;
    }
    if (data.Config) {
      this.applyConfig(this.decodeConfig(data.Config));
    }
  }

  /**
   * Returns the secret key for encryption.
   * When feature flag is OFF, returns the environment-hardcoded value.
   */
  getSecretKey(): string | null {
    if (!this.isFeatureEnabled()) {
      return (environment as any).secretKey ?? null;
    }
    return this.secretKey;
  }

  /**
   * Returns the FlexKey for Flexmonster licensing.
   * When feature flag is OFF, returns the environment-hardcoded value.
   */
  getFlexKey(): string[] {
    if (!this.isFeatureEnabled()) {
      const envFlexKey = (environment as any).flexKey;
      if (envFlexKey) {
        return this.normalizeFlexKey(envFlexKey);
      }
      throw new Error('RuntimeConfigService: flexKey not available in environment configuration.');
    }
    if (this.flexKey == null) {
      throw new Error('RuntimeConfigService: flexKey not loaded. Ensure config is fetched before using Flexmonster components.');
    }
    return this.flexKey;
  }

  /**
   * Returns true when config is available (either from API or environment fallback).
   */
  isConfigLoaded(): boolean {
    if (!this.isFeatureEnabled()) return true;
    return this.secretKey != null && this.flexKey != null;
  }

  clearConfig(): void {
    this.secretKey = null;
    this.flexKey = null;
  }

  private decodeConfig(base64Config: string): RuntimeConfig {
    const decoded = atob(base64Config);
    return JSON.parse(decoded) as RuntimeConfig;
  }

  private applyConfig(config: RuntimeConfig): void {
    if (config.SecretKey) {
      this.secretKey = config.SecretKey;
    }
    if (config.FlexKey != null) {
      this.flexKey = this.normalizeFlexKey(config.FlexKey);
    }
  }

  private normalizeFlexKey(value: string | string[]): string[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return value.split(',').map((k) => k.trim()).filter(Boolean);
    }
    return [];
  }
}
